import MaterialReactTable from 'material-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button, Col, Container, FormGroup, Input, Label, Row, Spinner,
} from 'reactstrap'
import moment from 'moment'
import Select from '../../components/Template/Input/Select'
import oikosApi from '../../services/oikos'
import { getRegs, selectValues } from '../../utils/fc'
import { notifyError, notifySuccess } from '../../utils/notify'
import qs from 'qs'

const WP_FIELDS = {
  wordpress_id: 'wordpress_id',
  title: 'title',
  client_name: 'client_name',
  permalink: 'permalink',
  start_year: 'start_year',
  location_raw: 'location_raw',
  state_raw: 'state_raw',
  excerpt: 'excerpt',
  description_text: 'description_text',
}

const parseServicoIds = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.map(Number).filter(n => !Number.isNaN(n))
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.map((item) => {
          if (item && typeof item === 'object' && item.value != null) return Number(item.value)
          return Number(item)
        }).filter(n => !Number.isNaN(n))
      }
    } catch (e) {
      return []
    }
  }
  return []
}

const nowSql = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const formatApiError = (err) => {
  const d = err?.data
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (d?.message) return String(d.message)
  if (d?.error) return String(d.error)
  if (Array.isArray(d) && d[0]) return String(d[0])
  if (err?.message) return err.message
  return null
}

/** PHP insertData/updateData concatena sem escape — dobrar ' evita SQL quebrado (ex.: D'Ávila). */
const escapeOikosSqlPayload = (data) => {
  const out = {}
  Object.keys(data || {}).forEach((key) => {
    const value = data[key]
    out[key] = typeof value === 'string' ? value.replace(/'/g, "''") : value
  })
  return out
}

const isLinkActive = (row) => row && Number(row.projCaseTeste_ativo) === 1

const findActiveLink = (rows, predicate) => {
  if (!Array.isArray(rows)) return null
  return rows.find(row => isLinkActive(row) && predicate(row)) || null
}

const findInactiveLink = (rows, predicate) => {
  if (!Array.isArray(rows)) return null
  return rows.find(row => !isLinkActive(row) && predicate(row)) || null
}

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map(uf => ({ value: uf, label: uf }))

const resolveUfOption = (raw) => {
  if (raw == null || raw === '') return null
  const s = String(raw).trim().toUpperCase()
  const exact = UF_OPTIONS.find(o => o.value === s)
  if (exact) return exact
  const match = s.match(/\b([A-Z]{2})\b/)
  if (match) return UF_OPTIONS.find(o => o.value === match[1]) || null
  return null
}

/** Menu no body evita corte pela tabela; select preenche a largura da célula */
const tableSelectProps = {
  classNamePrefix: 'select2-selection',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999, minWidth: 280 }),
    container: (base) => ({ ...base, width: '100%' }),
    control: (base) => ({
      ...base,
      minHeight: 30,
      fontSize: 12,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 4px',
    }),
    singleValue: (base) => ({
      ...base,
      maxWidth: 'calc(100% - 8px)',
      fontSize: 12,
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: 12,
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontSize: 11,
      padding: '1px 4px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 28,
    }),
    clearIndicator: (base) => ({ ...base, padding: 2 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  },
}

const emptyCaseContent = () => ({
  resumo: '',
  desafio: '',
  atuacao: '',
  resultado: '',
  depoimento: '',
  ind1_nome: '',
  ind1_valor: '',
  ind2_nome: '',
  ind2_valor: '',
  ind3_nome: '',
  ind3_valor: '',
})

const INDICADOR_HINTS = [
  '1 · Escala territorial ou física (ex.: hectares, municípios, áreas monitoradas)',
  '2 · Esforço técnico e operacional (ex.: campanhas, pontos amostrais, horas de campo)',
  '3 · Entrega ou resultado (ex.: relatórios, condicionantes, licenças)',
]

const trimOrNull = (v, max) => {
  const s = v == null ? '' : String(v).trim()
  if (!s) return null
  return max ? s.slice(0, max) : s
}

function CaseDetailPanel({
  row,
  initialContent,
  busy,
  onSave,
  permalink,
  hint,
}) {
  const [form, setForm] = useState(() => ({ ...emptyCaseContent(), ...(initialContent || {}) }))
  const [dirty, setDirty] = useState(false)
  const contentKey = JSON.stringify(initialContent || {})

  useEffect(() => {
    setForm({ ...emptyCaseContent(), ...(initialContent || {}) })
    setDirty(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey])

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setDirty(true)
  }

  const clienteCase = row?.client_name || '—'
  const clienteProjeto = row?.pj_nomefanta || row?.draftProj?.pj_nomefanta || '—'

  const fieldLabelStyle = { fontSize: 12, fontWeight: 600 }
  const inputStyle = { fontSize: 13 }

  return (
    <div style={{ padding: '8px 16px 16px', maxWidth: 960 }} onClick={e => e.stopPropagation()}>
      <Row className="mb-3">
        <Col md={6}>
          <div className="text-muted mb-1" style={fieldLabelStyle}>
            Cliente Case
          </div>
          <div style={{ fontSize: 13 }}>{clienteCase}</div>
        </Col>
        <Col md={6}>
          <div className="text-muted mb-1" style={fieldLabelStyle}>
            Cliente Projeto
          </div>
          <div style={{ fontSize: 13 }}>{clienteProjeto}</div>
        </Col>
      </Row>

      <div className="text-muted mb-1" style={fieldLabelStyle}>
        Resumo do case
      </div>
      <Input
        type="textarea"
        rows={4}
        value={form.resumo}
        disabled={busy}
        onChange={(e) => setField('resumo', e.target.value)}
        placeholder="Resumo do case..."
        style={inputStyle}
        className="mb-3"
      />

      <Row>
        <Col md={6} className="mb-3">
          <div className="text-muted mb-1" style={fieldLabelStyle}>Desafio</div>
          <Input
            type="textarea"
            rows={4}
            value={form.desafio}
            disabled={busy}
            onChange={(e) => setField('desafio', e.target.value)}
            placeholder="Qual era o desafio do cliente?"
            style={inputStyle}
          />
        </Col>
        <Col md={6} className="mb-3">
          <div className="text-muted mb-1" style={fieldLabelStyle}>Atuação</div>
          <Input
            type="textarea"
            rows={4}
            value={form.atuacao}
            disabled={busy}
            onChange={(e) => setField('atuacao', e.target.value)}
            placeholder="O que a Ecossis fez neste case?"
            style={inputStyle}
          />
        </Col>
        <Col md={6} className="mb-3">
          <div className="text-muted mb-1" style={fieldLabelStyle}>Resultado</div>
          <Input
            type="textarea"
            rows={4}
            value={form.resultado}
            disabled={busy}
            onChange={(e) => setField('resultado', e.target.value)}
            placeholder="Qual foi o resultado / entrega?"
            style={inputStyle}
          />
        </Col>
        <Col md={6} className="mb-3">
          <div className="text-muted mb-1" style={fieldLabelStyle}>Depoimento</div>
          <Input
            type="textarea"
            rows={4}
            value={form.depoimento}
            disabled={busy}
            onChange={(e) => setField('depoimento', e.target.value)}
            placeholder="Depoimento do cliente (texto)"
            style={inputStyle}
          />
        </Col>
      </Row>

      <div className="text-muted mb-2" style={fieldLabelStyle}>
        Indicadores do case
      </div>
      <p className="text-muted mb-2" style={{ fontSize: 12 }}>
        Três dimensões: escala territorial/física, esforço técnico/operacional e entrega/resultado.
      </p>
      {[1, 2, 3].map((n) => (
        <Row key={n} className="mb-2 align-items-end">
          <Col md={7}>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>
              {INDICADOR_HINTS[n - 1]}
            </div>
            <Input
              bsSize="sm"
              disabled={busy}
              value={form[`ind${n}_nome`]}
              onChange={(e) => setField(`ind${n}_nome`, e.target.value)}
              placeholder="Nome do indicador"
              maxLength={120}
              style={inputStyle}
            />
          </Col>
          <Col md={5}>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>Valor</div>
            <Input
              bsSize="sm"
              disabled={busy}
              value={form[`ind${n}_valor`]}
              onChange={(e) => setField(`ind${n}_valor`, e.target.value)}
              placeholder="Ex.: 120 ha, 15 campanhas…"
              maxLength={120}
              style={inputStyle}
            />
          </Col>
        </Row>
      ))}

      <div className="d-flex align-items-center mt-3" style={{ gap: 12 }}>
        <Button
          color="primary"
          size="sm"
          disabled={busy || !dirty}
          onClick={async () => {
            const ok = await onSave(form)
            if (ok !== false) setDirty(false)
          }}
        >
          {busy ? 'Salvando...' : 'Salvar conteúdo do case'}
        </Button>
        {hint && (
          <span className="text-muted" style={{ fontSize: 12 }}>{hint}</span>
        )}
      </div>
      {permalink && (
        <div className="mt-2" style={{ fontSize: 12 }}>
          <a href={permalink} target="_blank" rel="noopener noreferrer">
            Abrir no site
          </a>
        </div>
      )}
    </div>
  )
}

function VincularCases() {
  const [loading, setLoading] = useState(true)
  const [savingWpId, setSavingWpId] = useState(null)
  const [wpProjects, setWpProjects] = useState([])
  const [links, setLinks] = useState([])
  const [allLinks, setAllLinks] = useState([])
  const [projOptions, setProjOptions] = useState([])
  const [areaAtuaOptions, setAreaAtuaOptions] = useState([])
  const [servicoOptions, setServicoOptions] = useState([])
  const [filter, setFilter] = useState('pendentes')
  const [drafts, setDrafts] = useState({})

  const linksByWpId = useMemo(() => {
    const map = new Map()
    links.forEach((row) => {
      if (row.wordpress_id != null) map.set(Number(row.wordpress_id), row)
    })
    return map
  }, [links])

  const usedProjIds = useMemo(() => {
    const set = new Set()
    allLinks.forEach((row) => {
      if (isLinkActive(row) && row.proj_id != null) set.add(Number(row.proj_id))
    })
    return set
  }, [allLinks])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [wpRes, linkRows, allLinkRows, projs, areas, servicos] = await Promise.all([
        oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'import_wordpress_projects',
          fields: WP_FIELDS,
        })}`),
        getRegs('projCaseTeste', {}, {
          '(SELECT proj_nome FROM proj WHERE proj.proj_id = projCaseTeste.proj_id)': 'proj_nome',
          '(SELECT pj_nomefanta FROM pj WHERE pj.pj_id = (SELECT contrato_contratante FROM contrato WHERE contrato.contrato_id = (SELECT contrato_id FROM proj WHERE proj.proj_id = projCaseTeste.proj_id)))': 'pj_nomefanta',
          '(SELECT areaAtua_area FROM areaAtua WHERE areaAtua.areaAtua_id = projCaseTeste.areaAtua_id)': 'areaAtua_nome',
        }),
        getRegs('projCaseTeste', {}, {
          projCaseTeste_id: 'projCaseTeste_id',
          proj_id: 'proj_id',
          wordpress_id: 'wordpress_id',
          projCaseTeste_ativo: 'projCaseTeste_ativo',
        }, false),
        getRegs('proj', {}, {
          '(SELECT contrato_num FROM contrato WHERE contrato.contrato_id = proj.contrato_id)': 'contrato_num',
          '(SELECT pj_nomefanta FROM pj WHERE pj.pj_id = (SELECT contrato_contratante FROM contrato WHERE contrato.contrato_id = proj.contrato_id))': 'pj_nomefanta',
        }),
        selectValues('areaAtua', 'areaAtua_id', 'areaAtua_area'),
        selectValues('servico', 'servico_id', 'servico_nome'),
      ])

      setWpProjects(Array.isArray(wpRes.data) ? wpRes.data : [])
      setLinks(Array.isArray(linkRows) ? linkRows : [])
      setAllLinks(Array.isArray(allLinkRows) ? allLinkRows : [])
      setProjOptions(
        (Array.isArray(projs) ? projs : []).map(p => ({
          value: p.proj_id,
          label: p.contrato_num
            ? `${p.proj_nome || 'Sem nome'} (${p.contrato_num})`
            : (p.proj_nome || `Projeto #${p.proj_id}`),
          proj_nome: p.proj_nome,
          pj_nomefanta: p.pj_nomefanta || null,
        })),
      )
      setAreaAtuaOptions(Array.isArray(areas) ? areas : [])
      setServicoOptions(Array.isArray(servicos) ? servicos : [])
    } catch (err) {
      console.error('VincularCases load error:', err)
      notifyError('Não foi possível carregar cases WordPress ou vínculos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const getDraft = useCallback((wpId) => {
    const key = String(wpId)
    return drafts[key] || {
      proj: null,
      areaAtua: null,
      servicos: [],
      cidade: undefined,
      estado: undefined,
      caseContent: undefined,
    }
  }, [drafts])

  const setDraft = useCallback((wpId, patch) => {
    const key = String(wpId)
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {
          proj: null,
          areaAtua: null,
          servicos: [],
          cidade: undefined,
          estado: undefined,
          caseContent: undefined,
        }),
        ...patch,
      },
    }))
  }, [])

  const linkToCaseContent = (link, wp) => ({
    resumo: link?.projCaseTeste_resumo
      || wp?.description_text
      || wp?.excerpt
      || '',
    desafio: link?.projCaseTeste_desafio || '',
    atuacao: link?.projCaseTeste_atuacao || '',
    resultado: link?.projCaseTeste_resultado || '',
    depoimento: link?.projCaseTeste_depoimentoTexto || '',
    ind1_nome: link?.projCaseTeste_ind1_nome || '',
    ind1_valor: link?.projCaseTeste_ind1_valor || '',
    ind2_nome: link?.projCaseTeste_ind2_nome || '',
    ind2_valor: link?.projCaseTeste_ind2_valor || '',
    ind3_nome: link?.projCaseTeste_ind3_nome || '',
    ind3_valor: link?.projCaseTeste_ind3_valor || '',
  })

  const caseContentToPayload = (content) => ({
    projCaseTeste_resumo: trimOrNull(content?.resumo),
    projCaseTeste_desafio: trimOrNull(content?.desafio),
    projCaseTeste_atuacao: trimOrNull(content?.atuacao),
    projCaseTeste_resultado: trimOrNull(content?.resultado),
    projCaseTeste_depoimentoTexto: trimOrNull(content?.depoimento),
    projCaseTeste_ind1_nome: trimOrNull(content?.ind1_nome, 120),
    projCaseTeste_ind1_valor: trimOrNull(content?.ind1_valor, 120),
    projCaseTeste_ind2_nome: trimOrNull(content?.ind2_nome, 120),
    projCaseTeste_ind2_valor: trimOrNull(content?.ind2_valor, 120),
    projCaseTeste_ind3_nome: trimOrNull(content?.ind3_nome, 120),
    projCaseTeste_ind3_valor: trimOrNull(content?.ind3_valor, 120),
  })

  const tableRows = useMemo(() => {
    const rows = wpProjects.map((wp) => {
      const link = linksByWpId.get(Number(wp.wordpress_id))
      const draft = getDraft(wp.wordpress_id)
      const servicoIds = link ? parseServicoIds(link.projCaseTeste_servico) : []
      const linkedServicos = servicoIds
        .map(id => servicoOptions.find(o => Number(o.value) === Number(id)))
        .filter(Boolean)
      const linkedArea = link?.areaAtua_id != null
        ? areaAtuaOptions.find(o => Number(o.value) === Number(link.areaAtua_id)) || (
          link.areaAtua_nome
            ? { value: link.areaAtua_id, label: link.areaAtua_nome }
            : null
        )
        : null
      const linkedProj = link?.proj_id != null
        ? projOptions.find(o => Number(o.value) === Number(link.proj_id)) || {
          value: link.proj_id,
          label: link.proj_nome || `Projeto #${link.proj_id}`,
          pj_nomefanta: link.pj_nomefanta,
        }
        : null

      const linkCidade = link?.projCaseTeste_cidade ?? ''
      const linkEstado = resolveUfOption(link?.projCaseTeste_uf)
      const draftCidade = draft.cidade !== undefined ? draft.cidade : (wp.location_raw || '')
      const draftEstado = draft.estado !== undefined
        ? draft.estado
        : resolveUfOption(wp.state_raw)

      const linkCaseContent = linkToCaseContent(link, wp)
      const draftCaseContent = draft.caseContent !== undefined
        ? { ...emptyCaseContent(), ...draft.caseContent }
        : undefined

      return {
        ...wp,
        vinculado: Boolean(link),
        projCaseTeste_id: link?.projCaseTeste_id || null,
        proj_id: link?.proj_id || null,
        proj_nome: link?.proj_nome || null,
        pj_nomefanta: link?.pj_nomefanta || draft.proj?.pj_nomefanta || null,
        linkAreaAtua: linkedArea,
        linkServicos: linkedServicos,
        linkCidade,
        linkEstado,
        draftProj: draft.proj,
        draftAreaAtua: draft.areaAtua,
        draftServicos: draft.servicos,
        draftCidade,
        draftEstado,
        linkedProj,
        linkCaseContent,
        draftCaseContent,
        vinculadoEm: link?.projCaseTeste_dtCria || null,
      }
    })

    let filtered = rows
    if (filter === 'pendentes') filtered = rows.filter(r => !r.vinculado)
    else if (filter === 'vinculados') filtered = rows.filter(r => r.vinculado)

    if (filter === 'vinculados') {
      return [...filtered].sort((a, b) => {
        const da = a.vinculadoEm ? new Date(a.vinculadoEm).getTime() : 0
        const db = b.vinculadoEm ? new Date(b.vinculadoEm).getTime() : 0
        if (da !== db) return db - da
        return Number(b.projCaseTeste_id) - Number(a.projCaseTeste_id)
      })
    }
    return filtered
  }, [wpProjects, linksByWpId, filter, getDraft, servicoOptions, areaAtuaOptions, projOptions])

  const saveLink = useCallback(async (row) => {
    if (!row?.wordpress_id || row.vinculado) return

    const draft = getDraft(row.wordpress_id)
    const projOption = draft.proj
    if (!projOption?.value) {
      notifyError('Selecione um projeto antes de confirmar.')
      return
    }

    const cidadeRaw = draft.cidade !== undefined ? draft.cidade : (row.location_raw || '')
    const cidade = String(cidadeRaw || '').trim().slice(0, 128) || null
    const estadoOpt = draft.estado !== undefined
      ? draft.estado
      : resolveUfOption(row.state_raw)
    const uf = estadoOpt?.value ? String(estadoOpt.value).slice(0, 2) : null
    const caseContent = draft.caseContent !== undefined
      ? { ...emptyCaseContent(), ...draft.caseContent }
      : {
        ...emptyCaseContent(),
        resumo: row.description_text || row.excerpt || '',
      }

    const projId = Number(projOption.value)
    const wpId = Number(row.wordpress_id)

    const projConflict = findActiveLink(
      allLinks,
      l => l.proj_id != null && Number(l.proj_id) === projId,
    )
    if (projConflict) {
      notifyError(`Projeto #${projId} já está vinculado ao case WP ${projConflict.wordpress_id}.`)
      return
    }

    const wpConflict = findActiveLink(
      allLinks,
      l => l.wordpress_id != null && Number(l.wordpress_id) === wpId,
    )
    if (wpConflict) {
      notifyError(`Case WP ${wpId} já está vinculado ao projeto #${wpConflict.proj_id}.`)
      return
    }

    const inactiveRow = findInactiveLink(
      allLinks,
      l => (l.wordpress_id != null && Number(l.wordpress_id) === wpId)
        || (l.proj_id != null && Number(l.proj_id) === projId),
    )

    setSavingWpId(row.wordpress_id)
    try {
      const payload = {
        proj_id: projId,
        wordpress_id: wpId,
        projCaseTeste_nome: row.title || `Case ${row.wordpress_id}`,
        projCaseTeste_titulo: row.title || null,
        ...caseContentToPayload(caseContent),
        projCaseTeste_ano: row.start_year || null,
        projCaseTeste_cidade: cidade,
        projCaseTeste_uf: uf,
        projCaseTeste_servico: JSON.stringify((draft.servicos || []).map(s => s.value)),
        projCaseTeste_ativo: 1,
        projCaseTeste_dtMod: nowSql(),
      }
      if (draft.areaAtua?.value != null) {
        payload.areaAtua_id = Number(draft.areaAtua.value)
      }

      let savedId = null
      if (inactiveRow?.projCaseTeste_id) {
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projCaseTeste',
          data: escapeOikosSqlPayload({
            ...payload,
            projCaseTeste_id: inactiveRow.projCaseTeste_id,
          }),
        })
        if (!res?.ok && res?.problem) throw res
        savedId = inactiveRow.projCaseTeste_id
      } else {
        const res = await oikosApi.post('/', {
          action: 'insert',
          table: 'projCaseTeste',
          data: escapeOikosSqlPayload({
            ...payload,
            projCaseTeste_dtCria: nowSql(),
          }),
        })
        savedId = res?.data?.[0]?.projCaseTeste_id
        if (!savedId) {
          throw new Error('Insert sem retorno de ID')
        }
      }

      notifySuccess(inactiveRow ? 'Case revinculado ao projeto.' : 'Case vinculado ao projeto.')
      setDraft(row.wordpress_id, {
        proj: null,
        areaAtua: null,
        servicos: [],
        cidade: undefined,
        estado: undefined,
        caseContent: undefined,
      })
      await loadData()
    } catch (err) {
      console.error('VincularCases save error:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível salvar o vínculo: ${detail}`
          : 'Não foi possível salvar o vínculo. Projeto ou case já vinculado?',
      )
    } finally {
      setSavingWpId(null)
    }
  }, [getDraft, setDraft, loadData, allLinks])

  const updateLinkMeta = useCallback(async (row, patch) => {
    if (!row?.projCaseTeste_id) return false
    setSavingWpId(row.wordpress_id)
    try {
      const payload = {
        projCaseTeste_id: row.projCaseTeste_id,
        projCaseTeste_dtMod: nowSql(),
      }
      if (patch.areaAtua !== undefined) {
        payload.areaAtua_id = patch.areaAtua?.value ?? null
      }
      if (patch.servicos !== undefined) {
        payload.projCaseTeste_servico = JSON.stringify((patch.servicos || []).map(s => s.value))
      }
      if (patch.cidade !== undefined) {
        payload.projCaseTeste_cidade = String(patch.cidade || '').trim().slice(0, 128) || null
      }
      if (patch.estado !== undefined) {
        payload.projCaseTeste_uf = patch.estado?.value
          ? String(patch.estado.value).slice(0, 2)
          : null
      }
      if (patch.caseContent !== undefined) {
        Object.assign(payload, caseContentToPayload(patch.caseContent))
      }
      const res = await oikosApi.post('/', {
        action: 'update',
        table: 'projCaseTeste',
        data: escapeOikosSqlPayload(payload),
      })
      if (!res?.ok && res?.problem) {
        throw res
      }
      await loadData()
      return true
    } catch (err) {
      console.error('VincularCases update meta error:', err)
      notifyError('Não foi possível atualizar os dados do vínculo.')
      return false
    } finally {
      setSavingWpId(null)
    }
  }, [loadData])

  const saveCaseContent = useCallback(async (row, content) => {
    const next = { ...emptyCaseContent(), ...(content || {}) }
    setDraft(row.wordpress_id, { caseContent: next })

    if (row.vinculado && row.projCaseTeste_id) {
      const ok = await updateLinkMeta(row, { caseContent: next })
      if (ok) notifySuccess('Conteúdo do case salvo.')
      return ok
    }

    notifySuccess('Conteúdo atualizado nesta tela. Será gravado no vínculo ao confirmar.')
    return true
  }, [setDraft, updateLinkMeta])

  const unlinkCase = useCallback(async (row) => {
    if (!row?.projCaseTeste_id) return

    setSavingWpId(row.wordpress_id)
    try {
      const id = row.projCaseTeste_id
      let cleared = false

      try {
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projCaseTeste',
          data: {
            projCaseTeste_id: id,
            proj_id: null,
            wordpress_id: null,
            areaAtua_id: null,
            projCaseTeste_ativo: 0,
            projCaseTeste_dtMod: nowSql(),
          },
        })
        if (!res?.ok && res?.problem) throw res
        cleared = true
      } catch (clearErr) {
        console.warn('VincularCases unlink clear keys failed, trying soft delete', clearErr)
      }

      if (!cleared) {
        const res = await oikosApi.post('/', {
          action: 'delete',
          table: 'projCaseTeste',
          data: { projCaseTeste_id: id },
        })
        if (!res?.ok && res?.problem) throw res
      }

      const stillActive = await getRegs('projCaseTeste', { projCaseTeste_id: id })
      if (Array.isArray(stillActive) && stillActive.length > 0) {
        throw new Error('O registro continua ativo após desvincular.')
      }

      notifySuccess('Case desvinculado.')
      setDraft(row.wordpress_id, {
        proj: null,
        areaAtua: null,
        servicos: [],
        cidade: undefined,
        estado: undefined,
        caseContent: undefined,
      })
      await loadData()
    } catch (err) {
      console.error('VincularCases unlink error:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível desvincular: ${detail}`
          : 'Não foi possível desvincular o case.',
      )
    } finally {
      setSavingWpId(null)
    }
  }, [setDraft, loadData])

  const columns = useMemo(() => [
    {
      accessorKey: 'title',
      header: 'Nome do Case',
      size: 155,
      Cell: ({ cell }) => {
        const v = cell.getValue() || ''
        return (
          <span
            title={v}
            style={{
              display: 'block',
              fontSize: 12,
              lineHeight: 1.3,
              wordBreak: 'break-word',
              paddingRight: 10,
            }}
          >
            {v}
          </span>
        )
      },
    },
    {
      id: 'vincular_projeto',
      header: 'Vincular Projeto',
      size: 280,
      grow: 1.4,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingWpId === r.wordpress_id
        if (r.vinculado) {
          const nome = r.proj_nome || r.linkedProj?.label || `Projeto #${r.proj_id}`
          return (
            <span
              title={nome}
              style={{
                display: 'block',
                fontSize: 12,
                lineHeight: 1.3,
                wordBreak: 'break-word',
                paddingLeft: 4,
              }}
            >
              {nome}
            </span>
          )
        }
        const available = projOptions.filter(
          o => !usedProjIds.has(Number(o.value))
            || Number(o.value) === Number(r.draftProj?.value),
        )
        return (
          <div style={{ width: '100%', paddingLeft: 4 }} onClick={e => e.stopPropagation()}>
            <Select
              {...tableSelectProps}
              isSearchable
              isClearable
              isDisabled={busy}
              placeholder="Selecionar projeto..."
              options={available}
              value={r.draftProj}
              onChange={(opt) => {
                setDraft(r.wordpress_id, { proj: opt })
              }}
            />
            {r.draftProj?.value && usedProjIds.has(Number(r.draftProj.value)) && (
              <div className="text-danger mt-1" style={{ fontSize: 10 }}>
                Projeto já vinculado.
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'setor',
      header: 'Setor',
      size: 120,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingWpId === r.wordpress_id
        const value = r.vinculado ? r.linkAreaAtua : r.draftAreaAtua
        return (
          <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
            <Select
              {...tableSelectProps}
              isSearchable
              isClearable
              isDisabled={busy}
              placeholder="Setor..."
              options={areaAtuaOptions}
              value={value}
              onChange={(opt) => {
                if (r.vinculado) {
                  updateLinkMeta(r, { areaAtua: opt })
                } else {
                  setDraft(r.wordpress_id, { areaAtua: opt })
                }
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'servicos',
      header: 'Serviços',
      size: 140,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingWpId === r.wordpress_id
        const value = r.vinculado ? r.linkServicos : r.draftServicos
        return (
          <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
            <Select
              {...tableSelectProps}
              isMulti
              isSearchable
              isClearable
              isDisabled={busy}
              placeholder="Serviços..."
              options={servicoOptions}
              value={value}
              onChange={(opts) => {
                const next = opts || []
                if (r.vinculado) {
                  updateLinkMeta(r, { servicos: next })
                } else {
                  setDraft(r.wordpress_id, { servicos: next })
                }
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'cidade',
      header: 'Cidade',
      size: 100,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingWpId === r.wordpress_id
        const draft = getDraft(r.wordpress_id)
        const value = r.vinculado
          ? (draft.cidade !== undefined ? draft.cidade : (r.linkCidade || ''))
          : (r.draftCidade || '')
        return (
          <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
            <Input
              bsSize="sm"
              disabled={busy}
              value={value}
              title={value}
              placeholder="Cidade"
              style={{ fontSize: 12 }}
              onChange={(e) => {
                setDraft(r.wordpress_id, { cidade: e.target.value })
              }}
              onBlur={(e) => {
                if (r.vinculado) {
                  const next = e.target.value
                  if (next !== (r.linkCidade || '')) {
                    updateLinkMeta(r, { cidade: next })
                  }
                }
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'estado',
      header: 'UF',
      size: 68,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingWpId === r.wordpress_id
        const value = r.vinculado ? r.linkEstado : r.draftEstado
        return (
          <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
            <Select
              {...tableSelectProps}
              isSearchable
              isClearable
              isDisabled={busy}
              placeholder="UF"
              options={UF_OPTIONS}
              value={value}
              onChange={(opt) => {
                if (r.vinculado) {
                  updateLinkMeta(r, { estado: opt })
                } else {
                  setDraft(r.wordpress_id, { estado: opt })
                }
              }}
            />
          </div>
        )
      },
    },
    {
      accessorKey: 'vinculadoEm',
      header: 'Vinculado',
      size: 95,
      Cell: ({ cell }) => {
        const v = cell.getValue()
        return (
          <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
            {v ? moment(v).format('DD/MM/YY HH:mm') : '—'}
          </span>
        )
      },
    },
    {
      id: 'acoes',
      header: 'Ações',
      size: 72,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingWpId === r.wordpress_id
        if (r.vinculado) {
          return (
            <div onClick={e => e.stopPropagation()}>
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                style={{ fontSize: 10, lineHeight: 1.2 }}
                disabled={busy}
                onClick={() => unlinkCase(r)}
                title="Desvincular"
              >
                Desvinc.
              </Button>
            </div>
          )
        }
        const projBlocked = r.draftProj?.value
          && usedProjIds.has(Number(r.draftProj.value))
        if (!r.draftProj?.value) {
          return <span className="text-muted" style={{ fontSize: 11 }}>—</span>
        }
        return (
          <div onClick={e => e.stopPropagation()}>
            <Button
              color="primary"
              size="sm"
              style={{ fontSize: 10, padding: '1px 5px', lineHeight: 1.2 }}
              disabled={busy || projBlocked}
              onClick={() => saveLink(r)}
              title="Confirmar vínculo"
            >
              {busy ? '...' : 'Vinc.'}
            </Button>
          </div>
        )
      },
    },
  ], [
    savingWpId,
    projOptions,
    usedProjIds,
    areaAtuaOptions,
    servicoOptions,
    setDraft,
    getDraft,
    saveLink,
    updateLinkMeta,
    unlinkCase,
  ])
  return (
    <Container fluid style={{ paddingTop: '200px' }}>
      <Row className="mb-3 align-items-end">
        <Col md={8}>
          <h4 className="mb-1">Vincular Cases WordPress → Projetos</h4>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            Lista os cases importados e grava o vínculo em `projCaseTeste` (1 projeto por case).
            Expanda a linha para editar resumo, desafio, atuação, resultado, depoimento e indicadores.
          </p>
        </Col>
        <Col md={4} className="text-md-right">
          <FormGroup className="mb-0">
            <Label className="mr-2 mb-0">Filtro</Label>
            <Input
              type="select"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ maxWidth: 220, display: 'inline-block' }}
            >
              <option value="pendentes">Somente pendentes</option>
              <option value="vinculados">Somente vinculados</option>
              <option value="todos">Todos</option>
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex align-items-center text-muted py-4">
          <Spinner size="sm" className="mr-2" /> Carregando...
        </div>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={tableRows}
          layoutMode="grid"
          enableColumnActions={false}
          enableColumnFilters
          enableGlobalFilter
          enableExpanding
          enablePagination={tableRows.length > 25}
          initialState={{
            density: 'compact',
            pagination: { pageSize: 25, pageIndex: 0 },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: { width: '100%' },
          }}
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
              width: '100%',
            },
          }}
          muiTableContainerProps={{
            sx: {
              overflowX: 'hidden',
              width: '100%',
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: 12,
              px: 0.75,
              py: 0.5,
              overflow: 'hidden',
              verticalAlign: 'top',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontSize: 12,
              px: 0.75,
              py: 0.5,
              whiteSpace: 'nowrap',
            },
          }}
          displayColumnDefOptions={{
            'mrt-row-expand': {
              size: 36,
              header: '',
            },
          }}
          renderDetailPanel={({ row }) => {
            const r = row.original
            const initialContent = r.draftCaseContent !== undefined
              ? r.draftCaseContent
              : (r.linkCaseContent || emptyCaseContent())
            return (
              <CaseDetailPanel
                key={`${r.wordpress_id}-${r.projCaseTeste_id || 'p'}-${r.vinculadoEm || 'x'}`}
                row={r}
                initialContent={initialContent}
                busy={savingWpId === r.wordpress_id}
                permalink={r.permalink}
                hint={r.vinculado
                  ? 'Salva em projCaseTeste (resumo, desafio, atuação, resultado, depoimento e indicadores).'
                  : 'Em pendentes, o conteúdo fica na tela até confirmar o vínculo.'}
                onSave={(content) => saveCaseContent(r, content)}
              />
            )
          }}
        />
      )}
    </Container>
  )
}

export default VincularCases
